import { Component, Event, EventEmitter, Host, Listen, Method, Prop, State, h } from '@stencil/core';
import { getApplicationName, getBaseUrl, getFacadeUrl } from '../../../utils/api-utils';
import { getUser } from '../../../utils/auth-utils';
import { ComponentContentType } from '../../../types/site';
import { syQuerySelector } from '../../../utils/dom.utils';
import type { UIApiConfig } from '@sydle/ui-api';
import { getConfig } from '@sydle/ui-utils';
import { getLanguagePath } from '../../../utils/router-utils';

type ModalMessages = {
  successDialogTitle: string;
  successDialogMessage: string;
  alreadySubscribedDialogTitle: string;
  alreadySubscribedDialogMessage: string;
};

@Component({
  tag: 'sy-site-header',
  styleUrl: './scss/index.scss',
  shadow: true,
})
export class SySiteHeader {
  private user = getUser();
  private ctaButton: HTMLSyButtonElement;
  private sidebar: HTMLSySiteSidebarElement;
  private burger: HTMLSySiteBurgerIconElement;

  @State() logoHref = '/';

  @State() searchHref = `/blog/search`;

  @State() hideUserTab = false;

  /**
   * Event emitted on nav button click
   */
  @Event() sySiteHeaderNavigate: EventEmitter;

  /**
   * Event emitted on logout
   */
  @Event() sySiteHeaderDidLogout: EventEmitter;

  /**
   * Event emitted on site cta click
   */
  @Event({ bubbles: true, composed: true, eventName: 'sySignInWallOpen' }) sySignInWallOpen: EventEmitter<void>;

  /**
   * Text used for logout button
   */
  @Prop() logoutText = 'Sair';

  /**
   * Enable to use backlink
   */
  @Prop() backLink = false;

  /**
   * Link that backLink leads to
   */
  @Prop() backLinkHref = '/';

  /**
   * Title of cta header
   */
  @Prop() ctaTitle;

  /**
   * Icon of cta header
   */
  @Prop() ctaIcon;

  /**
   * Link that CTA leads to
   */
  @Prop() ctaHref;

  /**
   * The newsletter component from which to take the information and to which scroll on click
   */
  @Prop() newsletterComponent: ComponentContentType;

  /**
   * Enable to use blog search button
   */
  @Prop() searchButton = false;

  /**
   * Use to add path alternates urls
   */
  @Prop() appsPaths: string;

  /**
   * Use to add path alternates urls
   */
  @Prop() menuConfig: any;

  @Listen('sySiteBurgerIconDidOpen')
  handleSidebarOpen(): void {
    this.sidebar.show();
  }

  @Listen('sySiteBurgerIconDidClose')
  handleSidebarClose(): void {
    this.sidebar.hide();
  }

  @Listen('sySiteAlertModalDidClose')
  onModalClose(): void {
    this.ctaButton.disabled = false;
    this.ctaButton.loading = false;
  }

  private enableCtaButton = () => {
    this.ctaButton.disabled = false;
    this.ctaButton.loading = false;
  };

  /**
   * Gets the burguer to close
   */
  @Method()
  async sidebarClosed(): Promise<void> {
    this.burger.close();
  }

  private handleCtaClick() {
    if (this.ctaHref) {
      window.location.assign(this.ctaHref);
    }
    // Newsletter
    this.ctaButton.disabled = true;
    this.ctaButton.loading = true;

    if (this.newsletterComponent) {
      const newslettersToSubscribe = (getConfig<UIApiConfig>('sydle-ui-blog') as any)?.consentListsByLanguage?.map(
        (consent) => {
          return { _id: consent._id };
        },
      );

      if (this.user) {
        const messages = {
          successDialogTitle: this.newsletterComponent.settings.successDialogTitle,
          successDialogMessage: this.newsletterComponent.settings.successDialogMessage,
          alreadySubscribedDialogTitle: this.newsletterComponent.settings.alreadySubscribedDialogTitle,
          alreadySubscribedDialogMessage: this.newsletterComponent.settings.alreadySubscribedDialogMessage,
        };
        this.subscribeToNewsletter(newslettersToSubscribe || [], messages);
      } else {
        sessionStorage.setItem('newslettersToSubscribeHeader', JSON.stringify(newslettersToSubscribe));
        this.sySignInWallOpen.emit();
      }

      this.ctaButton.blur();
    } else {
      if (!this.user) {
        this.sySignInWallOpen.emit();
      }
    }
  }

  private subscribeToNewsletter(items: string[], messages: ModalMessages): void {
    const modal = syQuerySelector('sy-site-alert-modal') as any;
    (window as any).blogFacade
      .registerUserConsents({ consents: items })
      .then((_output) => _output.data?.newSubscriptions)
      .then((newSubscriptions) => {
        if (newSubscriptions && newSubscriptions.length > 0) {
          modal.dialogTitle = messages.successDialogTitle;
          modal.dialogMessage = messages.successDialogMessage;
        } else {
          modal.dialogTitle = messages.alreadySubscribedDialogTitle;
          modal.dialogMessage = messages.alreadySubscribedDialogMessage;
        }
        modal.open();
      });
  }

  private getImageUrl(classId: string, objectId: string, photoId: string): string {
    const token = getUser()?.accessToken?.token;
    const link = `${getBaseUrl()}${getApplicationName()}/_classId/${classId}/_download/${objectId}/${photoId}${
      token ? `?accessToken=${token}` : ''
    }`;
    return link;
  }

  componentWillRender() {
    this.searchHref = `${getLanguagePath()}/blog/search`;
    this.logoHref = `${getLanguagePath()}/`;
  }

  componentDidLoad() {
    if (this.user) {
      const newslettersToSubscribe =
        sessionStorage.getItem('newslettersToSubscribeHeader') &&
        JSON.parse(sessionStorage.getItem('newslettersToSubscribeHeader') || '');
      if (newslettersToSubscribe && this.newsletterComponent) {
        const messages = {
          successDialogTitle: this.newsletterComponent.settings.successDialogTitle,
          successDialogMessage: this.newsletterComponent.settings.successDialogMessage,
          alreadySubscribedDialogTitle: this.newsletterComponent.settings.alreadySubscribedDialogTitle,
          alreadySubscribedDialogMessage: this.newsletterComponent.settings.alreadySubscribedDialogMessage,
        };
        this.subscribeToNewsletter(newslettersToSubscribe, messages);
        sessionStorage.removeItem('newslettersToSubscribeHeader');
      }

      localStorage.setItem('siteUser', JSON.stringify(this.user));
    }
  }

  connectedCallback() {
    document.body?.addEventListener('sySignInWallDidClose', this.enableCtaButton);
  }

  disconnectedCallback() {
    document.body?.removeEventListener('sySignInWallDidClose', this.enableCtaButton);
  }

  render() {
    return (
      <Host>
        <sy-site-alert-modal />
        <sy-site-sidebar
          entries={this.menuConfig}
          ref={(el: HTMLSySiteSidebarElement) => {
            this.sidebar = el;
          }}
          appsPaths={this.appsPaths}
        />
        <div id="fixed-navbar" data-sy-viewport>
          <div class="nav-button block">
            {this.backLink ? (
              <a href={this.backLinkHref} aria-label="Voltar">
                <sy-icon
                  name="arrow-left"
                  path="assets/icons"
                  family="streamline-light"
                  role="img"
                  aria-label="arrow left"
                ></sy-icon>
                <sy-text>Voltar</sy-text>
              </a>
            ) : (
              <sy-site-burger-icon
                ref={(el: HTMLSySiteBurgerIconElement) => {
                  this.burger = el;
                }}
              />
            )}
          </div>

          <div class="logo block">
            <img src={`${getFacadeUrl()}/getLogo`} alt="SYDLE" onClick={() => window.location.assign(this.logoHref)} />
          </div>

          <div class="user-toolbar block">
            {this.ctaTitle && (this.user ? this.ctaTitle.toLowerCase() != 'login' : true) && (
              <sy-button
                ref={(el: HTMLSyButtonElement) => {
                  this.ctaButton = el;
                }}
                onClick={() => this.handleCtaClick()}
                icon={this.ctaIcon}
                content="icon-right"
                target="_self"
                variant="outlined"
                color="primary"
              >
                {this.ctaTitle}
              </sy-button>
            )}

            {this.user && !this.hideUserTab
              ? [
                  <sy-avatar
                    small
                    rounded
                    class="user-avatar"
                    image={
                      this.user.photo?._id
                        ? this.getImageUrl(this.user._class._id, this.user.code, this.user.photo._id)
                        : undefined
                    }
                    key="avatar"
                    initials={this.user.name.toUpperCase()[0]}
                    initialsSize={1}
                  ></sy-avatar>,
                  <sy-dropdown toggle-by=".user-avatar" placement="bottom-end" container=".user-avatar" key="dropdown">
                    <sy-site-logout-dropdown-content logoutText={this.logoutText}></sy-site-logout-dropdown-content>
                  </sy-dropdown>,
                ]
              : null}
            {this.searchButton ? (
              <a href={this.searchHref}>
                <sy-icon class="search-icon" name="search" size="24"></sy-icon>
              </a>
            ) : null}
          </div>
        </div>
      </Host>
    );
  }
}
